// Order Management Type Definitions
// These interfaces match the backend Pydantic schemas in order_schemas.py

// Customer Interfaces
export interface CustomerBase {
  name: string;
  email?: string | null;
  phone?: string | null;
  billing_address?: string | null;
  notes?: string | null;
}

export interface CustomerCreate extends CustomerBase {
  customer_number?: string | null;
}

export interface CustomerUpdate {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  billing_address?: string | null;
  notes?: string | null;
  is_active?: boolean | null;
}

export interface Customer extends CustomerBase {
  id: number;
  customer_number?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Shipping Address Interfaces
export interface ShippingAddressBase {
  recipient_name: string;
  address_line1: string;
  address_line2?: string | null;
  city: string;
  state?: string | null;
  postal_code: string;
  country: string;
  phone?: string | null;
}

export interface ShippingAddressCreate extends ShippingAddressBase {
  customer_id?: number | null;
  is_default?: boolean;
}

export interface ShippingAddressUpdate {
  recipient_name?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  phone?: string | null;
  is_default?: boolean | null;
}

export interface ShippingAddress extends ShippingAddressBase {
  id: number;
  customer_id?: number | null;
  is_default: boolean;
  created_at: string;
}

// Order Item Interfaces
export interface OrderItemBase {
  product_id: number;
  quantity: number;
  unit_price: number;
  notes?: string | null;
}

export interface OrderItemCreate extends OrderItemBase {}

export interface OrderItemUpdate {
  quantity?: number | null;
  unit_price?: number | null;
  notes?: string | null;
}

export interface OrderItem extends OrderItemBase {
  id: number;
  order_id: number;
  sku: string;
  product_name: string;
  line_total: number;
  created_at: string;
}

// Order Interfaces
export interface OrderBase {
  customer_id?: number | null;
  status?: string;
  payment_status?: string;
  warehouse_id?: number | null;
  notes?: string | null;
}

export interface OrderCreate extends OrderBase {
  customer?: CustomerCreate | null;
  shipping_address: ShippingAddressCreate;
  items: OrderItemCreate[];
}

export interface OrderUpdate {
  status?: string | null;
  payment_status?: string | null;
  notes?: string | null;
  customer_id?: number | null;
  shipping_address_id?: number | null;
  warehouse_id?: number | null;
}

export interface OrderStatusUpdate {
  status: string;
  notes?: string | null;
  // Shipping details captured when moving an order to "shipped"
  delivery_method?: string | null; // 'partner' | 'hand_delivery' | 'custom'
  shipping_provider?: string | null;
  awb_number?: string | null;
  // Delivery reference no. for hand-delivered orders marked "delivered";
  // stored as the order's AWB.
  delivery_reference?: string | null;
}

export interface Order extends OrderBase {
  id: number;
  order_number: string;
  order_date: string;
  subtotal: number;
  tax_amount: number;
  shipping_amount: number;
  total_amount: number;
  shipping_address_id?: number | null;
  external_order_id?: string | null;
  external_system?: string | null;
  
  // Additional comprehensive fields
  channel?: string | null;
  order_type?: string | null;
  ship_by_date?: string | null;
  delivery_slot?: string | null;
  order_tags?: string | null;
  ship_date?: string | null;
  on_hold?: boolean | null;
  verification_status?: string | null;
  fulfillment_location?: string | null;
  currency?: string | null;
  external_status?: string | null;
  sub_order_id?: string | null;
  order_source?: string | null;
  pick_instructions?: string | null;
  ship_instructions?: string | null;
  
  created_by?: number | null;
  updated_by?: number | null;
  created_at: string;
  updated_at: string;
  customer?: Customer | null;
  shipping_address?: ShippingAddress | null;
  items: OrderItem[];
}

// Order Status History Interfaces
export interface OrderStatusHistoryBase {
  order_id: number;
  from_status?: string | null;
  to_status: string;
  changed_by?: number | null;
  notes?: string | null;
}

export interface OrderStatusHistoryCreate extends OrderStatusHistoryBase {}

export interface OrderStatusHistory extends OrderStatusHistoryBase {
  id: number;
  created_at: string;
}

// Import/Export Interfaces
export interface OrderImportRequest {
  format: string; // vineretail, magento
}

export interface ImportError {
  row?: number | null;
  field: string;
  message: string;
  order_number?: string | null;
  type?: string | null;
}

export interface ImportResult {
  success_count: number;
  error_count: number;
  duplicate_count: number;
  errors: ImportError[];
  imported_order_ids: number[];
}

// Search and Filter Interfaces
export interface OrderSearchParams {
  q?: string | null;
  order_number?: string | null;
  customer_name?: string | null;
  status?: string | null;
  channel?: string | null;
  date_from?: string | null;
  date_to?: string | null;
  skip?: number;
  limit?: number;
  sort_by?: string; // order_date, order_number, customer_name, total_amount
  sort_order?: string; // asc, desc
}

// List Response Interfaces
export interface OrderListItem {
  id: number;
  order_number: string;
  customer_name?: string | null;
  order_date: string;
  status: string;
  total_amount: number;
}

export interface OrderListResponse {
  orders: OrderListItem[];
  total: number;
  skip: number;
  limit: number;
}

// Order Status Constants
export const ORDER_STATUSES = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  ALLOCATED: 'allocated',
  SHIPPED: 'shipped',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled',
} as const;

export type OrderStatus = typeof ORDER_STATUSES[keyof typeof ORDER_STATUSES];

// Payment Status Constants
export const PAYMENT_STATUSES = {
  PENDING: 'pending',
  PAID: 'paid',
  FAILED: 'failed',
  REFUNDED: 'refunded',
} as const;

export type PaymentStatus = typeof PAYMENT_STATUSES[keyof typeof PAYMENT_STATUSES];

// Import Format Constants
export const IMPORT_FORMATS = {
  VINERETAIL: 'vineretail',
  MAGENTO: 'magento',
  CUSTOM: 'custom',
} as const;

export type ImportFormat = typeof IMPORT_FORMATS[keyof typeof IMPORT_FORMATS];
